import { Component, Input } from "@angular/core";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamLocation } from "@common/ADAPT.Common.Model/organisation/team-location";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { DxUtilities } from "@common/lib/utilities/dx-utilities";
import { cacheLatest, emptyIfUndefinedOrNull } from "@common/lib/utilities/rxjs-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { ITeamHierarchyNode, TeamHierarchyNodeType, TeamHierarchyService } from "@org-common/lib/teams/team-hierarchy.service";
import { ItemClickEvent, ItemRenderedEvent } from "devextreme/ui/tree_view";
import { BehaviorSubject, combineLatest, map, merge, Observable, startWith } from "rxjs";
import { switchMap } from "rxjs/operators";

@Component({
    selector: "adapt-team-hierarchy-dashboard-element",
    templateUrl: "./team-hierarchy-dashboard-element.component.html",
    styleUrls: ["./team-hierarchy-dashboard-element.component.scss"],
    standalone: false,
})
export class TeamHierarchyDashboardElementComponent extends BaseComponent {
    public readonly TeamHierarchyNodeType = TeamHierarchyNodeType;

    @Input()
    public set team(value: Team) {
        if (value) {
            this.team$.next(value);
        }
    }

    @Input()
    public set showMoreContext(value: boolean) {
        this.showMoreContext$.next(value);
    }

    @Input() public showPeople = true;

    public team$ = new BehaviorSubject<Team | undefined>(undefined);
    public hierarchy$: Observable<ITeamHierarchyNode[]>;

    protected showMoreContext$ = new BehaviorSubject(false);

    constructor(
        teamHierarchyService: TeamHierarchyService,
        rxjsBreezeService: RxjsBreezeService,
    ) {
        super();

        const entityChange$ = merge(
            rxjsBreezeService.entityTypeChanged(TeamLocation),
            rxjsBreezeService.entityTypeChanged(Team),
        ).pipe(
            startWith(undefined),
        );
        const hierarchy$ = combineLatest([
            this.team$.pipe(emptyIfUndefinedOrNull()),
            entityChange$,
        ]).pipe(
            switchMap(([team]) => teamHierarchyService.promiseToBuildHierarchyForTeam(team)),
            cacheLatest(),
        );
        this.hierarchy$ = combineLatest([hierarchy$, this.showMoreContext$]).pipe(
            map(([nodes, showMoreContext]) => showMoreContext
                ? nodes
                : this.filterIndirectSiblings(nodes)),
        );
    }

    private filterIndirectSiblings(nodes: ITeamHierarchyNode[]): ITeamHierarchyNode[] {
        return nodes.flatMap((node) => {
            // Keep the current team and its full hierarchy below it.
            if (node.selected) {
                return [node];
            }

            const children = this.filterIndirectSiblings(node.children);
            return node.isParentOfTeam || children.length > 0
                ? [{ ...node, children }]
                : [];
        });
    }

    public isHierarchyNode(data: any): data is ITeamHierarchyNode {
        return "type" in data && "children" in data;
    }

    public asHierarchyNode(data: any) {
        if (this.isHierarchyNode(data)) {
            return data as ITeamHierarchyNode;
        }

        return undefined;
    }

    public async onItemClicked(event: ItemClickEvent) {
        if (!event.node?.expanded) {
            await event.component.expandItem(event.itemData);
        } else {
            await event.component.collapseItem(event.itemData);
        }
    }

    public async onItemRendered(event: ItemRenderedEvent) {
        if (this.isHierarchyNode(event.itemData) && event.itemData.isParentOfTeam) {
            const element = DxUtilities.getHtmlElement<HTMLElement>(event.itemElement);
            if (element) {
                element.classList.add("is-parent-of-current-team");
            }
        }
    }
}
